import { AlertTriangle, AtSign, CheckCircle2, Inbox, Pencil, RefreshCw, Send, Trash2, Users } from "lucide-react";
import { useMemo, useState, type JSX } from "react";
import { Link } from "react-router-dom";

import type { GroupDTO, MailAddressDTO, MailAddressKind, MemberDTO } from "../../common/api/types.js";
import { PrimaryButton } from "../../common/components/PrimaryButton.js";
import { useAdminDomains, useAdminGroups, useAdminMembers, useCreateMailAddress, useDeleteMailAddress, useMailAddresses, useResyncMailAddress, useUpdateMailAddress } from "../adminQueries.js";
import { MailTemplateCard } from "./MailTemplateCard.js";

/** Was die drei Arten bedeuten, in den Worten der Oberfläche. */
const KINDS: Record<MailAddressKind, { label: string; hint: string; icon: JSX.Element }> = {
  SHARED: { label: "Geteiltes Postfach", hint: "Ein Posteingang für alle Mitglieder, etwa support@. Antworten gehen unter dieser Adresse hinaus.", icon: <Inbox className="h-5 w-5" /> },
  DISTRIBUTION: { label: "Verteiler", hint: "Jedes Mitglied bekommt eine Kopie in sein eigenes Postfach.", icon: <Send className="h-5 w-5" /> },
  ALIAS: { label: "Alias", hint: "Zweite Adresse für das Postfach einer Person, etwa info@.", icon: <AtSign className="h-5 w-5" /> },
};
const KIND_ORDER: MailAddressKind[] = ["SHARED", "DISTRIBUTION", "ALIAS"];

/** Was ein Formular über Mitglieder und Ziel weiß; gleich für Anlegen und Bearbeiten. */
interface Recipients {
  targetUserId: string;
  memberUserIds: string[];
  memberGroupIds: string[];
  external: string;
}

function emptyRecipients(): Recipients {
  return { targetUserId: "", memberUserIds: [], memberGroupIds: [], external: "" };
}

function recipientsOf(address: MailAddressDTO): Recipients {
  return {
    targetUserId: address.targetUserId ?? "",
    memberUserIds: address.members.flatMap((member) => (member.userId ? [member.userId] : [])),
    memberGroupIds: address.members.flatMap((member) => (member.groupId ? [member.groupId] : [])),
    external: address.externalRecipients.join("\n"),
  };
}

function externalList(value: string): string[] {
  return value.split(/[\s,;]+/).map((entry) => entry.trim()).filter(Boolean);
}

/** Nur das, was zur Art passt — sonst lehnt der Server ab. */
function bodyFor(kind: MailAddressKind, recipients: Recipients) {
  if (kind === "ALIAS") return { targetUserId: recipients.targetUserId };
  return {
    memberUserIds: recipients.memberUserIds,
    memberGroupIds: recipients.memberGroupIds,
    ...(kind === "DISTRIBUTION" ? { externalRecipients: externalList(recipients.external) } : {}),
  };
}

export function AdminMail(): JSX.Element {
  const addresses = useMailAddresses();
  const domains = useAdminDomains();
  const members = useAdminMembers();
  const groups = useAdminGroups();
  const verified = (domains.data ?? []).filter((domain) => domain.status === "VERIFIED");
  const active = useMemo(() => (members.data ?? []).filter((member) => member.status === "ACTIVE"), [members.data]);
  const byKind = KIND_ORDER.map((kind) => ({ kind, entries: (addresses.data ?? []).filter((address) => address.kind === kind) }));

  return (
    <div className="min-h-0 flex-1 space-y-4 overflow-y-auto bg-surface-sunken p-4 sm:p-6">
      {domains.isSuccess && verified.length === 0 ? (
        <section className="rounded-xl border border-line bg-surface-raised p-4 text-sm text-ink">
          <p className="font-bold">Noch keine bestätigte Domain</p>
          <p className="mt-1 text-ink-muted">Adressen lassen sich nur auf Domains anlegen, deren DNS-Einträge geprüft sind.</p>
          <Link to="?section=domains" className="mt-2 inline-block font-semibold text-brand hover:underline">Zu den Domains</Link>
        </section>
      ) : (
        <>
          <MailTemplateCard domains={verified.map((domain) => ({ id: domain.id, name: domain.domain }))} />
          <CreateForm domains={verified.map((domain) => ({ id: domain.id, name: domain.domain }))} members={active} groups={groups.data ?? []} />
        </>
      )}
      {addresses.error ? <p className="text-sm text-danger">{addresses.error.message}</p> : null}
      {byKind.map(({ kind, entries }) => entries.length > 0 ? (
        <section key={kind} className="space-y-2">
          <h3 className="text-xs font-bold uppercase tracking-wide text-ink-muted">{KINDS[kind].label}</h3>
          <div className="grid gap-3 lg:grid-cols-2">
            {entries.map((address) => <AddressCard key={address.id} address={address} members={active} groups={groups.data ?? []} />)}
          </div>
        </section>
      ) : null)}
      {addresses.isSuccess && addresses.data.length === 0 && verified.length > 0 ? <p className="text-sm text-ink-muted">Noch keine Adressen angelegt.</p> : null}
    </div>
  );
}

function CreateForm({ domains, members, groups }: { domains: Array<{ id: string; name: string }>; members: MemberDTO[]; groups: GroupDTO[] }): JSX.Element {
  const create = useCreateMailAddress();
  const [kind, setKind] = useState<MailAddressKind>("SHARED");
  const [localPart, setLocalPart] = useState("");
  const [domainId, setDomainId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [recipients, setRecipients] = useState<Recipients>(emptyRecipients());
  const domain = domainId || domains[0]?.id || "";
  const ready = localPart.trim() && displayName.trim() && domain && (kind !== "ALIAS" || recipients.targetUserId);
  const submit = () => create.mutate(
    { kind, localPart: localPart.trim(), domainId: domain, displayName: displayName.trim(), ...bodyFor(kind, recipients) },
    { onSuccess: () => { setLocalPart(""); setDisplayName(""); setRecipients(emptyRecipients()); } },
  );

  return (
    <section className="rounded-xl border border-line bg-surface-raised p-4">
      <h3 className="font-bold text-ink">Neue Adresse</h3>
      <div role="radiogroup" aria-label="Art der Adresse" className="mt-3 grid gap-2 sm:grid-cols-3">
        {KIND_ORDER.map((entry) => (
          <button key={entry} type="button" role="radio" aria-checked={kind === entry} onClick={() => setKind(entry)} className={kind === entry ? "rounded-xl border-2 border-brand bg-surface p-3 text-left" : "rounded-xl border border-line bg-surface p-3 text-left hover:bg-surface-sunken"}>
            <span className="flex items-center gap-2 font-bold text-ink"><span className="text-brand">{KINDS[entry].icon}</span>{KINDS[entry].label}</span>
            <span className="mt-1 block text-xs text-ink-muted">{KINDS[entry].hint}</span>
          </button>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input value={localPart} onChange={(event) => setLocalPart(event.target.value)} placeholder={kind === "ALIAS" ? "info" : kind === "SHARED" ? "support" : "team"} aria-label="Teil vor dem @" autoCapitalize="none" className="min-h-10 min-w-0 flex-1 basis-32 rounded-lg border border-line bg-surface px-3 text-sm" />
        <span className="text-ink-muted">@</span>
        <select value={domain} onChange={(event) => setDomainId(event.target.value)} aria-label="Domain" className="min-h-10 min-w-0 flex-1 basis-40 rounded-lg border border-line bg-surface px-3 text-sm">
          {domains.map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}
        </select>
      </div>
      <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} placeholder="Anzeigename, z. B. Kundensupport" aria-label="Anzeigename" className="mt-2 min-h-10 w-full rounded-lg border border-line bg-surface px-3 text-sm" />
      <RecipientFields kind={kind} value={recipients} onChange={setRecipients} members={members} groups={groups} />
      {create.error ? <p className="mt-2 text-sm text-danger">{create.error.message}</p> : null}
      <div className="mt-3 flex justify-end">
        <PrimaryButton disabled={!ready || create.isPending} onClick={submit}>Anlegen</PrimaryButton>
      </div>
    </section>
  );
}

function RecipientFields({ kind, value, onChange, members, groups }: { kind: MailAddressKind; value: Recipients; onChange: (value: Recipients) => void; members: MemberDTO[]; groups: GroupDTO[] }): JSX.Element {
  if (kind === "ALIAS") {
    return (
      <label className="mt-3 block text-xs font-bold uppercase tracking-wide text-ink-muted">
        Post landet bei
        <select value={value.targetUserId} onChange={(event) => onChange({ ...value, targetUserId: event.target.value })} className="mt-1 min-h-10 w-full rounded-lg border border-line bg-surface px-3 text-sm font-normal normal-case tracking-normal text-ink">
          <option value="">Person wählen</option>
          {members.map((member) => <option key={member.userId} value={member.userId}>{member.displayName} · {member.email}</option>)}
        </select>
      </label>
    );
  }
  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((entry) => entry !== id) : [...list, id]);
  return (
    <div className="mt-3 space-y-3">
      {groups.length > 0 ? (
        <div>
          <span className="text-xs font-bold uppercase tracking-wide text-ink-muted">Gruppen</span>
          <div className="mt-1 flex flex-wrap gap-2">
            {groups.map((group) => <Chip key={group.id} pressed={value.memberGroupIds.includes(group.id)} onClick={() => onChange({ ...value, memberGroupIds: toggle(value.memberGroupIds, group.id) })}><Users className="mr-1 inline h-3.5 w-3.5" />{group.name}</Chip>)}
          </div>
        </div>
      ) : null}
      <div>
        <span className="text-xs font-bold uppercase tracking-wide text-ink-muted">Personen</span>
        <div className="mt-1 flex max-h-40 flex-wrap gap-2 overflow-y-auto">
          {members.map((member) => <Chip key={member.userId} pressed={value.memberUserIds.includes(member.userId)} onClick={() => onChange({ ...value, memberUserIds: toggle(value.memberUserIds, member.userId) })}>{member.displayName}</Chip>)}
        </div>
      </div>
      {kind === "DISTRIBUTION" ? (
        <label className="block text-xs font-bold uppercase tracking-wide text-ink-muted">
          Externe Empfänger
          <textarea value={value.external} onChange={(event) => onChange({ ...value, external: event.target.value })} rows={2} placeholder="partner@beispiel.de, steuerberater@beispiel.de" className="mt-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm font-normal normal-case tracking-normal text-ink" />
        </label>
      ) : null}
    </div>
  );
}

function Chip({ pressed, onClick, children }: { pressed: boolean; onClick: () => void; children: JSX.Element | string | Array<JSX.Element | string> }): JSX.Element {
  return <button type="button" aria-pressed={pressed} onClick={onClick} className={pressed ? "rounded-full bg-brand px-3 py-1.5 text-xs font-bold text-brand-ink" : "rounded-full bg-surface-sunken px-3 py-1.5 text-xs font-semibold text-ink-muted hover:text-ink"}>{children}</button>;
}

function AddressCard({ address, members, groups }: { address: MailAddressDTO; members: MemberDTO[]; groups: GroupDTO[] }): JSX.Element {
  const update = useUpdateMailAddress();
  const resync = useResyncMailAddress();
  const remove = useDeleteMailAddress();
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState(address.displayName);
  const [recipients, setRecipients] = useState<Recipients>(recipientsOf(address));
  const startEditing = () => { setDisplayName(address.displayName); setRecipients(recipientsOf(address)); update.reset(); setEditing(true); };
  const save = () => update.mutate({ id: address.id, body: { displayName: displayName.trim(), ...bodyFor(address.kind, recipients) } }, { onSuccess: () => setEditing(false) });
  const confirmDelete = () => { if (window.confirm(`${address.address} wirklich löschen? Post an diese Adresse wird danach abgewiesen.`)) remove.mutate(address.id); };
  const who = address.kind === "ALIAS" ? (address.targetName ? [address.targetName] : []) : [...address.members.map((member) => member.name), ...address.externalRecipients];

  return (
    <section className="rounded-xl border border-line bg-surface-raised p-4">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 text-brand">{KINDS[address.kind].icon}</span>
        <div className="min-w-0 flex-1">
          <h4 className="break-all font-bold text-ink">{address.address}</h4>
          {editing ? (
            <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} aria-label="Anzeigename" className="mt-1 min-h-9 w-full rounded-lg border border-line bg-surface px-2 text-sm" />
          ) : (
            <p className="text-sm text-ink-muted">{address.displayName}</p>
          )}
        </div>
        {editing ? null : (
          <>
            <button type="button" onClick={startEditing} title="Bearbeiten" className="rounded-lg p-2 text-ink-muted hover:bg-surface-sunken hover:text-ink"><Pencil className="h-4 w-4" /></button>
            <button type="button" onClick={confirmDelete} disabled={remove.isPending} title="Löschen" className="rounded-lg p-2 text-ink-muted hover:bg-surface-sunken hover:text-danger"><Trash2 className="h-4 w-4" /></button>
          </>
        )}
      </div>
      {editing ? (
        <>
          <RecipientFields kind={address.kind} value={recipients} onChange={setRecipients} members={members} groups={groups} />
          {update.error ? <p className="mt-2 text-sm text-danger">{update.error.message}</p> : null}
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={() => setEditing(false)} className="rounded-lg border border-line px-3 py-2 text-sm font-semibold text-ink hover:bg-surface-sunken">Abbrechen</button>
            <PrimaryButton disabled={!displayName.trim() || (address.kind === "ALIAS" && !recipients.targetUserId) || update.isPending} onClick={save}>Speichern</PrimaryButton>
          </div>
        </>
      ) : (
        <>
          <p className="mt-3 text-xs font-bold uppercase tracking-wide text-ink-muted">{address.kind === "ALIAS" ? "Post landet bei" : "Mitglieder"}</p>
          {who.length > 0 ? (
            <ul className="mt-1 flex flex-wrap gap-1.5">{who.map((name) => <li key={name} className="rounded-full bg-surface-sunken px-2.5 py-1 text-xs text-ink">{name}</li>)}</ul>
          ) : (
            <button type="button" onClick={startEditing} className="mt-1 text-sm font-semibold text-brand hover:underline">Mitglieder hinzufügen</button>
          )}
          <SyncState address={address} busy={resync.isPending} onResync={() => resync.mutate(address.id)} />
          {remove.error ? <p className="mt-2 text-sm text-danger">{remove.error.message}</p> : null}
        </>
      )}
    </section>
  );
}

function SyncState({ address, busy, onResync }: { address: MailAddressDTO; busy: boolean; onResync: () => void }): JSX.Element {
  if (address.synced) {
    return <p className="mt-3 flex items-center gap-1.5 text-xs text-ink-muted"><CheckCircle2 className="h-4 w-4 text-emerald-600" />Auf dem Mailserver eingerichtet</p>;
  }
  return (
    <div className="mt-3 flex items-start gap-2 rounded-lg bg-surface-sunken p-2 text-xs">
      <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
      <span className="min-w-0 flex-1 text-ink">{address.syncError ?? "Noch nicht mit dem Mailserver abgeglichen."}</span>
      <button type="button" onClick={onResync} disabled={busy} className="flex shrink-0 items-center gap-1 font-semibold text-brand hover:underline disabled:opacity-50"><RefreshCw className={busy ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"} />Erneut abgleichen</button>
    </div>
  );
}
